<template>
  <div v-if="!isEmptyData || pageLoading" class="passthrough-report">
    <InsightHeader
      class="header"
      :loading="pageLoading"
      :reporting-message="reportingMessage"
      @close="setReportingMessage({ header: '', message: '', beaconArticleId: '' })"
    />
    <div class="passthrough-report-content">
      <PassthroughReportSummary
        class="summary mb-0"
        :loading="pageLoading"
        :data="data"
        :tooltips="tooltips"
        :project-name="projectName"
        :date-range="dateRange"
      />
      <PassthroughReportByStageWidget
        class="passthrough-by-stage"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <PassthroughReportCalculatedWidget
        class="passthrough-calculated"
        :data="data"
        :empty-data="isEmptyData"
        :error="error"
        :loading="pageLoading"
      />
      <PassthroughReportTable
        class="table"
        :error="error"
        :loading="pageLoading"
        :empty-data="isEmptyData"
        :data="data"
        :table-rows="passthroughTableRows"
        :downloading="downloading"
        @download-csv="downloadPassthroughTable"
      />
    </div>
    <FilterExportPreview class="mt-4" />
  </div>
  <NoDataForWidget v-else />
</template>

<script setup>
import {
  onMounted, onBeforeUnmount, computed, ref,
} from 'vue';
import { useFilters } from '@/stores/filters/filters';
import { useRoute, useRouter } from 'vue-router';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useAppBase } from '@/stores/app/base';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import NoDataForWidget from '@/components/insights/NoDataForWidget.vue';
import { usePassthroughDropoffReport } from '@/stores/insights/passthroughDropoffReport';
import PassthroughReportByStageWidget from '@/components/insights/ProcessInsights/PassthroughReport/PassthroughReportByStageWidget.vue';
import PassthroughReportCalculatedWidget from '@/components/insights/ProcessInsights/PassthroughReport/PassthroughReportCalculatedWidget.vue';
import PassthroughReportTable from '@/components/insights/ProcessInsights/PassthroughReport/PassthroughReportTable.vue';
import PassthroughReportSummary from '@/components/insights/ProcessInsights/PassthroughReport/PassthroughReportSummary.vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useInsights } from '@/stores/insights/insights';
import { useTooltips } from '@/stores/insights/tooltips';
import InsightHeader from '@/components/insights/InsightHeader.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';
import { useTracking } from '@/stores/tracking/tracking';
import FilterExportPreview from '@/components/insights/ExportModal/FilterExportPreview.vue';

const emit = defineEmits(['track-page-load']);

const {
  isEmptyData, data, passthroughTableRows, error,
} = storeToRefs(usePassthroughDropoffReport());
const { pageLoading } = storeToRefs(useAppLoading());
const { authData } = storeToRefs(useAppAuthData());
const { reportingMessage } = storeToRefs(useInsights());
const { tooltips } = storeToRefs(useTooltips());
const { dateRange } = storeToRefs(useDatePicker());

const { loadFilters, setFilterConfig } = useFilters();
const { setDatePickerVariant, resetDateRange } = useDatePicker();
const { loadPassthroughReport, downloadPassthroughCSV } = usePassthroughDropoffReport();
const { showFilterBarSettings } = useAppBase();
const { setReportingMessage } = useInsights();
const { setExportConfig, resetExportConfig } = useInsightsExport();
const { setShareConfig, resetShareConfig } = useInsightsShare();

const route = useRoute();
const router = useRouter();

const downloading = ref(false);

const projectName = computed(() => authData.value.projectName);

const loadPage = async (params) => Promise.all([
  loadFilters(params),
  loadPassthroughReport(params),
]);

const downloadPassthroughTable = async () => {
  downloading.value = true;

  await downloadPassthroughCSV({
    dateRange: dateRange.value,
    projectName: projectName.value,
  });

  useTracking().insightsDownloadReport({
    reportName: route.name,
    downloadType: 'Individual Widgets',
    downloadLocation: 'Individual Widget',
    fileType: 'CSV',
    widgetName: 'Data Table (.csv)',
  });

  downloading.value = false;
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  emit('track-page-load', { page: 'Pass Through' });

  setFilterConfig({
    module: 'insights',
    endpoint: 'process',
    datepicker: true,
    narrow: true,
    showMyJobs: true,
  });

  setDatePickerVariant('multiOption');
  showFilterBarSettings({ route: route.name, options: [] });

  setShareConfig({
    shareable: true,
    showReportImageButton: true,
  });

  setExportConfig({
    csvExport: true,
    pngExport: true,
    pngWidgetOptions: {
      'passthrough-overall': 'Metric: Pass-through Rate',
      'apps-for-one-hire': 'Metric: Applications per Hire',
      'passthrough-by-stage': 'Graph: Pass-through Rate by Stage',
      'yield-calculations': 'Graph: Applications per X Hires',
      'passthrough-report-table': 'Table: Pass-through Data',
    },
    csvOptions: [
      {
        label: 'Data Table (.csv)',
        csvDownloadFunction: () => {
          downloadPassthroughCSV({
            dateRange: dateRange.value,
            projectName: projectName.value,
          });
        },
      },
    ],
  });

  await loadPage({ routeQuery: route.query || '', selectedMyJobs: route.query?.owned === 'true', router });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  resetDateRange();

  resetExportConfig();

  resetShareConfig();
});
</script>

<style lang="less" scoped>
.passthrough-report {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding-bottom: 16px;
}

.passthrough-report-content {
  width: 100%;
  display: grid;
  grid-template-areas:
    "summary summary"
    "passthroughByStage passthroughCalculated"
    "table table";
  grid-template-rows: auto 1fr auto;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.summary {
  grid-area: summary;
}
.passthrough-by-stage {
  grid-area: passthroughByStage;
}
.passthrough-calculated {
  grid-area: passthroughCalculated;
}
.table {
  grid-area: table;
}
</style>
